import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Activity, Stethoscope, TrendingUp, BookOpen, FileText } from 'lucide-react';
import { VideoSection, DR_REDDY_VIDEOS } from '../../components/YouTubeEmbed';
import { useSite } from '../../contexts/SiteContext';

export const RotatorCuffTear = () => {
  const { theme } = useSite();
  
  const faqs = [
    {
      question: 'What is a rotator cuff tear?',
      answer: 'A rotator cuff tear is a complete or partial tear in one or more of the four tendons (supraspinatus, infraspinatus, subscapularis, teres minor) that form the rotator cuff. The supraspinatus tendon is most commonly affected. Research published in the Journal of Bone and Joint Surgery shows that tear prevalence increases with age, affecting up to 50% of people over 80 years old.'
    },
    {
      question: 'Can a rotator cuff tear heal on its own?',
      answer: 'According to systematic reviews, partial-thickness tears may stabilize or improve with conservative treatment, but complete (full-thickness) tears do not heal spontaneously. However, the MOON Shoulder Group studies demonstrate that many patients with tears can function well without surgery through structured rehabilitation that strengthens compensatory muscles.'
    },
    {
      question: 'How long is recovery after rotator cuff surgery?',
      answer: 'Evidence-based protocols recommend: immobilization for 4-6 weeks, active motion from 6-12 weeks, strengthening from 12 weeks onwards. The ASES outcomes research shows most patients achieve 80-90% improvement by 6 months, with continued gains up to 12 months. Full return to demanding activities typically requires 6-9 months.'
    },
    {
      question: 'What activities should I avoid with a rotator cuff tear?',
      answer: 'Research indicates avoiding: heavy lifting (>10kg), repetitive overhead movements, pushing/pulling activities, and sleeping on the affected side. Your treatment plan will include specific activity modification guidelines based on tear size and your functional goals.'
    },
    {
      question: 'Is arthroscopic surgery better than open surgery?',
      answer: 'Cochrane systematic reviews show equivalent healing rates between arthroscopic and open techniques. However, arthroscopic surgery offers advantages: smaller incisions (5-10mm vs 4-6cm), less deltoid muscle damage, reduced post-operative pain, and faster early recovery. Most rotator cuff repairs today are performed arthroscopically.'
    },
    {
      question: 'What is the success rate of rotator cuff repair?',
      answer: 'Published outcomes data shows 85-95% of patients report significant improvement in pain and function after surgery. Healing rates vary by tear size: small tears (<1cm) have 90%+ healing, medium tears (1-3cm) have 70-85% healing, large/massive tears (>3cm) have 50-70% healing. Biological factors like age, diabetes, and smoking also affect outcomes.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="rotator-cuff-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Rotator Cuff Tear Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600">
              Arthroscopic repair & recovery by Dr. Deepthi Nandan Reddy at Apollo Hospitals. 85-95% success rate.
            </p>
          </div>
        </div>
      </section>

      {/* What is it */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Understanding Rotator Cuff Tears
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                The rotator cuff comprises four muscles and their tendons—supraspinatus, infraspinatus, subscapularis, and teres minor—that envelope the humeral head, providing dynamic stability and enabling the complex movements of the shoulder joint.
              </p>
              <p>
                <strong>Epidemiology:</strong> Rotator cuff pathology is extremely common. MRI studies of asymptomatic individuals show that partial-thickness tears affect 4% of people under 40, rising to 54% over age 60. Full-thickness tears are present in 25% of people in their 60s and over 50% in their 80s (Yamamoto et al., JBJS 2010).
              </p>
              <p>
                <strong>The Clinical Challenge:</strong> The presence of a tear does not always correlate with symptoms. Many people function normally despite having significant tears, while others experience severe disability from small tears. This has led to a paradigm shift toward individualized, function-based treatment rather than tear-based treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Classification */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Classification of Rotator Cuff Tears
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    By Depth (Ellman Classification)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-3"><strong>Partial-Thickness Tears:</strong></p>
                  <p className="text-sm text-gray-600">• Grade 1: &lt;3mm deep (&lt;25% thickness)</p>
                  <p className="text-sm text-gray-600">• Grade 2: 3-6mm deep (25-50% thickness)</p>
                  <p className="text-sm text-gray-600">• Grade 3: &gt;6mm deep (&gt;50% thickness)</p>
                  <p className="text-gray-600 mt-3 mb-3"><strong>Full-Thickness Tears:</strong></p>
                  <p className="text-sm text-gray-600">• Complete tear through entire tendon</p>
                  <p className="text-sm text-gray-600">• May be contained or retracted</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    By Size (DeOrio & Cofield)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-3"><strong>Full-Thickness Tear Classification:</strong></p>
                  <p className="text-sm text-gray-600">• Small: &lt;1cm</p>
                  <p className="text-sm text-gray-600">• Medium: 1-3cm</p>
                  <p className="text-sm text-gray-600">• Large: 3-5cm</p>
                  <p className="text-sm text-gray-600">• Massive: &gt;5cm or involving 2+ tendons</p>
                  <p className="text-sm text-gray-500 mt-3">Size correlates with healing potential and surgical outcomes</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Etiology */}
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Degenerative Tears (Most Common)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-3">Progressive tendon failure due to:</p>
                  <p className="text-sm text-gray-600">• Age-related hypovascular zone degeneration</p>
                  <p className="text-sm text-gray-600">• Repetitive microtrauma and impingement</p>
                  <p className="text-sm text-gray-600">• Intrinsic tendon degeneration</p>
                  <p className="text-sm text-gray-600">• Subacromial bone spur contact</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Acute Traumatic Tears</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-3">Sudden tendon failure from:</p>
                  <p className="text-sm text-gray-600">• Fall on outstretched arm</p>
                  <p className="text-sm text-gray-600">• Forceful lifting or pulling</p>
                  <p className="text-sm text-gray-600">• Shoulder dislocation (especially &gt;40 years)</p>
                  <p className="text-sm text-gray-600">• Sports injuries or direct trauma</p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r">
              <p className="text-gray-700">
                <strong>Evidence Note:</strong> Studies show that most &ldquo;acute&rdquo; tears in patients over 40 actually occur in tendons with pre-existing degeneration. Pure traumatic tears in healthy tendons are rare and typically occur only with significant force.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Clinical Presentation
              </h2>
            </div>
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Characteristic Symptoms</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-gray-700">
                  <p>• <strong>Night pain:</strong> A hallmark symptom, often waking patients from sleep</p>
                  <p>• <strong>Painful arc:</strong> Pain between 60-120° of abduction (impingement zone)</p>
                  <p>• <strong>Weakness:</strong> Especially with overhead activities and external rotation</p>
                  <p>• <strong>Crepitus:</strong> Grinding or catching sensation with movement</p>
                  <p>• <strong>Stiffness:</strong> Particularly in chronic cases or with associated frozen shoulder</p>
                  <p>• <strong>Functional limitation:</strong> Difficulty with reaching, lifting, dressing</p>
                </CardContent>
              </Card>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-green-50">
                  <CardHeader>
                    <CardTitle className="text-green-800">Partial Tear Features</CardTitle>
                  </CardHeader>
                  <CardContent className="text-gray-700">
                    <p>• Pain often worse than weakness</p>
                    <p>• Activity-related symptoms</p>
                    <p>• May improve with rest</p>
                    <p>• Often responsive to conservative care</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50">
                  <CardHeader>
                    <CardTitle className="text-orange-800">Full-Thickness Tear Features</CardTitle>
                  </CardHeader>
                  <CardContent className="text-gray-700">
                    <p>• Weakness may be prominent</p>
                    <p>• Pseudo-paralysis in massive tears</p>
                    <p>• Atrophy visible in chronic cases</p>
                    <p>• Positive lag signs on examination</p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r">
                <p className="text-gray-700">
                  <strong>Important:</strong> Up to 40% of full-thickness rotator cuff tears are asymptomatic. The decision to treat should be based on symptoms and functional impairment, not just imaging findings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnosis */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Stethoscope className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Diagnostic Approach
              </h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Clinical Examination</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p><strong>Special Tests (Sensitivity/Specificity from systematic reviews):</strong></p>
                  <p>• <strong>Jobe&apos;s Test (Empty Can):</strong> Supraspinatus - 77%/68%</p>
                  <p>• <strong>External Rotation Lag Sign:</strong> Infraspinatus - 70%/100%</p>
                  <p>• <strong>Lift-off Test:</strong> Subscapularis - 62%/100%</p>
                  <p>• <strong>Bear Hug Test:</strong> Subscapularis - 60%/92%</p>
                  <p className="text-sm text-gray-500 mt-2">A combination of tests improves diagnostic accuracy</p>
                </CardContent>
              </Card>

              <p>
                <strong>X-rays:</strong> Cannot visualize soft tissue tears but provide valuable information about bone morphology (acromion shape, arthritis, bone spurs), superior humeral head migration in chronic massive tears, and calcific deposits.
              </p>
              <p>
                <strong>MRI (Gold Standard):</strong> Sensitivity 92%, Specificity 93% for full-thickness tears. Provides detailed assessment of tear size, tendon retraction, muscle atrophy (Goutallier classification), and fatty infiltration—all critical for surgical planning and prognostication.
              </p>
              <p>
                <strong>Ultrasound:</strong> In experienced hands, approaches MRI accuracy (sensitivity 92%, specificity 94%). Advantages include dynamic assessment, lower cost, and no radiation. Excellent for screening and monitoring.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Evidence-Based Treatment */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Evidence-Based Treatment Options
              </h2>
            </div>
            
            {/* Evidence Note */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r">
              <div className="flex items-start gap-2">
                <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Treatment recommendations based on AAOS Clinical Practice Guidelines, Cochrane systematic reviews, and published outcomes from MOON Shoulder Group and International Shoulder Registries.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">FIRST LINE</span>
                    Conservative Treatment
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Indications:</strong> Partial tears, chronic degenerative tears, low-demand patients, significant comorbidities</p>
                  <p><strong>Structured Rehabilitation Program:</strong></p>
                  <p>• Phase 1: Pain control, gentle ROM, scapular stabilization</p>
                  <p>• Phase 2: Progressive strengthening of deltoid and periscapular muscles</p>
                  <p>• Phase 3: Functional training and activity modification</p>
                  <p className="mt-3"><strong>Adjuncts:</strong></p>
                  <p>• NSAIDs for pain control (short-term)</p>
                  <p>• Subacromial corticosteroid injection (Grade B evidence) - limits of 3 injections recommended</p>
                  <p className="bg-green-50 p-3 rounded mt-3">
                    <strong>Evidence:</strong> The UKUFF trial showed that 75% of patients with small-to-medium tears improved significantly with physiotherapy alone, with no difference in outcomes compared to surgery at 2 years.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">SURGICAL OPTIONS</span>
                    Operative Treatment
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-3">
                  <p><strong>Indications for Surgery:</strong></p>
                  <p>• Acute traumatic tears in active patients</p>
                  <p>• Failed conservative treatment (3-6 months)</p>
                  <p>• Full-thickness tears with significant weakness</p>
                  <p>• Progressive tear enlargement on imaging</p>
                  
                  <div className="mt-4 space-y-4">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Arthroscopic Rotator Cuff Repair (Preferred)</p>
                      <p>• All-arthroscopic suture anchor technique</p>
                      <p>• Single-row vs double-row fixation based on tear pattern</p>
                      <p>• Advantages: Minimal soft tissue trauma, comprehensive joint evaluation, faster early recovery</p>
                      <p>• 85-95% patient satisfaction rates</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Mini-Open or Open Repair</p>
                      <p>• Reserved for massive tears with significant retraction</p>
                      <p>• Allows tendon mobilization and margin convergence</p>
                      <p>• Similar long-term outcomes to arthroscopic repair</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Irrepairable Tears: Salvage Options</p>
                      <p>• Superior Capsular Reconstruction (SCR)</p>
                      <p>• Partial repair with margin convergence</p>
                      <p>• Tendon transfers (latissimus dorsi, pectoralis major)</p>
                      <p>• Reverse total shoulder arthroplasty (elderly, arthritis)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Post-Operative Rehabilitation Protocol
            </h2>
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader>
                  <div className="text-2xl font-bold text-medical-orange mb-2">Weeks 0-6</div>
                  <CardTitle className="text-base">Protection Phase</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600">
                  • Sling immobilization<br/>
                  • Passive ROM only<br/>
                  • Pendulum exercises<br/>
                  • Grip strengthening
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-2xl font-bold text-medical-orange mb-2">Weeks 6-12</div>
                  <CardTitle className="text-base">Early Motion</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600">
                  • Active-assisted ROM<br/>
                  • Wean from sling<br/>
                  • Scapular exercises<br/>
                  • Light isometrics
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-2xl font-bold text-medical-orange mb-2">Weeks 12-20</div>
                  <CardTitle className="text-base">Strengthening</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600">
                  • Progressive resistance<br/>
                  • Rotator cuff strengthening<br/>
                  • Functional activities<br/>
                  • Core stability
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-2xl font-bold text-medical-orange mb-2">Months 5-9</div>
                  <CardTitle className="text-base">Return to Activity</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600">
                  • Sport-specific training<br/>
                  • Full strength work<br/>
                  • Gradual return to activities<br/>
                  • Maintenance program
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
              <p className="text-gray-700">
                <strong>Evidence-Based Timeline:</strong> Biological healing of tendon to bone requires 12-16 weeks minimum. Early aggressive rehabilitation increases re-tear rates. The balance between protecting the repair and preventing stiffness requires individualized progression based on tear size, repair quality, and tissue characteristics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prognosis */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Outcomes & Prognosis
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                <strong>Surgical Outcomes:</strong> Large registry studies and systematic reviews consistently show 85-95% patient satisfaction after rotator cuff repair. Functional scores (ASES, Constant) improve by 30-50 points on average.
              </p>
              <p>
                <strong>Healing Rates by Tear Size:</strong>
              </p>
              <div className="grid md:grid-cols-3 gap-4 my-4">
                <div className="bg-green-50 p-4 rounded text-center">
                  <p className="text-2xl font-bold text-green-700">90%+</p>
                  <p className="text-sm text-gray-600">Small tears (&lt;1cm)</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded text-center">
                  <p className="text-2xl font-bold text-yellow-700">70-85%</p>
                  <p className="text-sm text-gray-600">Medium tears (1-3cm)</p>
                </div>
                <div className="bg-orange-50 p-4 rounded text-center">
                  <p className="text-2xl font-bold text-orange-700">50-70%</p>
                  <p className="text-sm text-gray-600">Large/Massive tears</p>
                </div>
              </div>
              <p>
                <strong>Factors Affecting Outcome:</strong> Tear size, fatty infiltration (Goutallier grade), muscle atrophy, patient age, diabetes, smoking, and compliance with rehabilitation all influence healing and functional outcomes.
              </p>
              <p>
                <strong>Re-tear Rates:</strong> Overall re-tear rates range from 10-40% depending on tear characteristics. Importantly, patients with re-tears may still experience significant functional improvement compared to pre-operative status.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific References */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-gray-500" />
              <h3 className="text-xl font-bold text-gray-700">Scientific References & Guidelines</h3>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-sm text-gray-600 space-y-2">
              <p>1. AAOS Clinical Practice Guidelines on Rotator Cuff Problems</p>
              <p>2. Yamamoto A, et al. Prevalence and risk factors of a rotator cuff tear in the general population. J Shoulder Elbow Surg. 2010</p>
              <p>3. UKUFF Trial: Carr AJ, et al. Bone & Joint Journal. 2017</p>
              <p>4. MOON Shoulder Group Outcomes Database</p>
              <p>5. Cochrane Reviews: Surgical vs Conservative Treatment for Rotator Cuff Tears</p>
              <p>6. Goutallier D, et al. Fatty muscle degeneration in cuff ruptures. Clin Orthop. 1994</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">
                Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <VideoSection video={DR_REDDY_VIDEOS.rotatorCuff} theme={theme} />

      {/* FAQs */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <CTASection />
    </div>
  );
};
